import { useState, useEffect } from 'react';
import axios from 'axios';
import { History, RefreshCw, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
};

// Human-friendly labels for known audit actions
const ACTION_LABELS = {
  login: 'Iniciou sessão',
  login_falhado: 'Tentativa de login falhada',
  animal_criado: 'Criou animal',
  animal_editado: 'Editou animal',
  animal_removido: 'Removeu animal',
  animal_saida: 'Registou saída de animal',
  animal_regresso: 'Registou regresso de animal',
  utilizador_criado: 'Criou utilizador',
  utilizador_editado: 'Editou utilizador',
  utilizador_eliminado: 'Eliminou utilizador',
  password_alterada: 'Alterou password',
  backup_executado: 'Executou backup',
  backup_importado: 'Importou backup',
  backup_restaurado: 'Restaurou backup',
};

const ACTION_COLOR = {
  login: 'bg-blue-100 text-blue-700',
  login_falhado: 'bg-red-100 text-red-700',
  animal_criado: 'bg-green-100 text-green-700',
  animal_editado: 'bg-amber-100 text-amber-700',
  animal_removido: 'bg-red-100 text-red-700',
  utilizador_criado: 'bg-green-100 text-green-700',
  utilizador_editado: 'bg-amber-100 text-amber-700',
  utilizador_eliminado: 'bg-red-100 text-red-700',
  password_alterada: 'bg-purple-100 text-purple-700',
};

const PER_PAGE = 25;

export default function AuditoriaPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [pagina, setPagina] = useState(0);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  const carregar = () => {
    setLoading(true);
    axios.get(`${API}/audit/logs?limit=500`).then(r => setLogs(r.data)).catch(() => setLogs([])).finally(() => setLoading(false));
  };
  useEffect(() => { carregar(); }, []);

  const filtrados = logs.filter(l => {
    if (!filtro.trim()) return true;
    const q = filtro.toLowerCase();
    const label = (ACTION_LABELS[l.action] || l.action || '').toLowerCase();
    return (l.user_email || '').toLowerCase().includes(q)
      || (l.action || '').toLowerCase().includes(q)
      || label.includes(q)
      || JSON.stringify(l.detail || l.details || '').toLowerCase().includes(q);
  });

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / PER_PAGE));
  const paginaAtual = Math.min(pagina, totalPaginas - 1);
  const visiveis = filtrados.slice(paginaAtual * PER_PAGE, (paginaAtual + 1) * PER_PAGE);

  return (
    <div data-testid="auditoria-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
          <History className="w-6 h-6 text-primary" /> Auditoria
        </h2>
        <button onClick={carregar} data-testid="auditoria-refresh"
          className="flex items-center gap-1 bg-muted text-foreground rounded-full px-3 py-1.5 text-xs font-medium hover:bg-muted/80">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Registo de todas as ações relevantes na aplicação. Últimos 500 eventos.
      </p>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input data-testid="auditoria-filtro"
          placeholder="Procurar por email, ação ou detalhes…"
          value={filtro}
          onChange={e => { setFiltro(e.target.value); setPagina(0); }}
          className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-card text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
      </div>

      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        {loading ? (
          <p className="text-center text-muted-foreground py-8 text-sm">A carregar…</p>
        ) : visiveis.length === 0 ? (
          <p className="text-center text-muted-foreground py-8 text-sm">Sem registos.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground bg-muted/30">
                <tr>
                  <th className="text-left p-3">Data / Hora</th>
                  <th className="text-left p-3">Utilizador</th>
                  <th className="text-left p-3">Ação</th>
                  <th className="text-left p-3">Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((l, i) => {
                  const label = ACTION_LABELS[l.action] || l.action || '—';
                  const color = ACTION_COLOR[l.action] || 'bg-gray-100 text-gray-700';
                  const detail = l.detail || l.details || l.metadata || null;
                  return (
                    <tr key={l.id || `${l.timestamp}-${i}`} className="border-t border-border">
                      <td className="p-3 text-xs whitespace-nowrap">{fmtDateTime(l.timestamp)}</td>
                      <td className="p-3 text-xs">
                        <div className="font-medium">{l.user_email || <span className="text-muted-foreground">—</span>}</div>
                        {l.user_role && <div className="text-[10px] uppercase text-muted-foreground">{l.user_role}</div>}
                      </td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${color}`}>{label}</span>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground max-w-md">
                        {detail && typeof detail === 'object' ? (
                          <pre className="whitespace-pre-wrap break-all font-mono text-[11px] bg-muted/40 rounded p-1.5">{JSON.stringify(detail, null, 0)}</pre>
                        ) : (detail || <span className="opacity-60">—</span>)}
                        {l.entity_type && (
                          <div className="mt-0.5 text-[10px] uppercase">{l.entity_type}{l.entity_id ? ` · ${l.entity_id.slice(0, 8)}…` : ''}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPaginas > 1 && (
        <div className="flex justify-center items-center gap-2 mt-3">
          <button onClick={() => setPagina(p => Math.max(0, p - 1))} disabled={paginaAtual === 0}
            className="p-2 rounded-full border border-border bg-card hover:bg-muted disabled:opacity-30 text-sm">←</button>
          <span className="text-sm text-muted-foreground">Página {paginaAtual + 1} de {totalPaginas} · {filtrados.length} registos</span>
          <button onClick={() => setPagina(p => Math.min(totalPaginas - 1, p + 1))} disabled={paginaAtual >= totalPaginas - 1}
            className="p-2 rounded-full border border-border bg-card hover:bg-muted disabled:opacity-30 text-sm">→</button>
        </div>
      )}
    </div>
  );
}
